import { Overlay, OverlayConfig } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Injectable, Injector, TemplateRef, Type } from '@angular/core';
import { VDialogRef } from './v-dialog-ref';
import { VDialogComponent } from './v-dialog.component';

// https://codinglatte.com/posts/angular/reusable-modal-overlay-using-angular-cdk-overlay/

export type DialogContentType = 'template' | 'string' | 'component';

@Injectable({
    providedIn: 'root'
})
export class DialogService {
    constructor(private overlay: Overlay, private injector: Injector) {
    }

    open<R = any, T = any>(
        content: string | TemplateRef<any> | Type<any>,
        data: T,
        outputs?: { [outputName: string]: (value: any) => void }
    ): VDialogRef<R> {
        const configs = new OverlayConfig({
            hasBackdrop: true,
            panelClass: ['modal', 'is-active'],
            backdropClass: 'modal-background'
        });

        const overlayRef = this.overlay.create(configs);
        const dialogRef = new VDialogRef<R, T>(overlayRef, content, data);

        const injector = this.createInjector(dialogRef, this.injector);
        overlayRef.attach(new ComponentPortal(VDialogComponent, null, injector));

        if (outputs) {
            dialogRef.setOutputs(outputs); // Set outputs dynamically
        }

        return dialogRef;
    }

    getContentType(content: string | TemplateRef<any> | Type<any>): 'template' | 'string' | 'component' {
        if (typeof content === 'string') {
            return 'string';
        }
        else if (content instanceof TemplateRef) {
            return 'template';
        }
        else {
            return 'component';
        }
    }

    private createInjector(ref: VDialogRef, inj: Injector): Injector {
        return Injector.create({
            parent: inj,
            providers: [
                {
                    provide: VDialogRef,
                    useValue: ref,
                },
            ],
        });
    }
}
